import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  useToast,
  HStack,
  Text,
  Switch,
  Divider,
  Alert,
  AlertIcon,
  Code,
} from '@chakra-ui/react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase, isSupabaseConfigured } from '../../../utils/supabase';
import moment from 'moment';

export default function ScheduleMeetingModal({ isOpen, onClose, deal, contact }) {
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingTime, setMeetingTime] = useState('');
  const [meetingType, setMeetingType] = useState('phone');
  const [duration, setDuration] = useState('30');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sendInviteToContact, setSendInviteToContact] = useState(true);
  const [additionalAttendees, setAdditionalAttendees] = useState('');
  const [addMeetLink, setAddMeetLink] = useState(false);
  const toast = useToast();
  const { currentUser } = useAuth();

  const handleSubmit = async () => {
    if (!meetingDate || !meetingTime) {
      toast({
        title: 'Missing information',
        description: 'Please select date and time',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      if (!isSupabaseConfigured()) {
        throw new Error('Database not configured. Please contact support.');
      }

      const meetingDateTime = new Date(`${meetingDate}T${meetingTime}`);
      const meetingEndTime = new Date(meetingDateTime.getTime() + parseInt(duration) * 60000);
      const meetingLabel = `${meetingType.charAt(0).toUpperCase() + meetingType.slice(1)} meeting: ${deal.dealName}`;

      // Build attendees list
      const attendees = [];
      if (sendInviteToContact && contact?.email) {
        attendees.push(contact.email);
      }
      if (additionalAttendees.trim()) {
        // Parse comma-separated emails
        const additionalEmails = additionalAttendees
          .split(',')
          .map(e => e.trim())
          .filter(e => e.includes('@'));
        attendees.push(...additionalEmails);
      }

      // Create the meeting on the CRM calendar. Google Calendar sync (event,
      // invites, Meet link) happens automatically server-side via the outbox.
      const { error: eventError } = await supabase.from('crm_calendar_events').insert({
        user_id: currentUser.uid,
        created_by: currentUser.uid,
        title: meetingLabel,
        description: notes || `${meetingType} meeting scheduled via CRM`,
        event_type: 'meeting',
        start_at: meetingDateTime.toISOString(),
        end_at: meetingEndTime.toISOString(),
        all_day: false,
        attendees,
        add_video_link: addMeetLink,
        related_entity_type: 'deal',
        related_entity_id: deal.id,
        related_entity_name: deal.dealName,
        deal_id: deal.id,
      });
      if (eventError) throw eventError;

      // Companion to-do in the task list. google_sync_status='synced' keeps it
      // from creating a SECOND Google event (the meeting above owns that slot).
      const { error: taskError } = await supabase.from('crm_tasks').insert({
        title: meetingLabel,
        description: notes || `${meetingType} meeting scheduled`,
        type: 'crm',
        priority: 'high',
        status: 'pending',
        assigned_to: currentUser.uid,
        created_by: currentUser.uid,
        due_date: meetingDateTime.toISOString(),
        due_time: meetingTime,
        event_duration_minutes: parseInt(duration),
        is_calendar_event: true,
        google_sync_status: 'synced',
        related_entity_type: 'deal',
        related_entity_id: deal.id,
        related_entity_name: deal.dealName,
        deal_id: deal.id,
      });
      if (taskError) throw taskError;

      toast({
        title: 'Meeting scheduled',
        description: `${meetingType} meeting set for ${moment(meetingDateTime).format('MMM DD @ h:mm A')} — syncing to Google Calendar`,
        status: 'success',
        duration: 4000,
      });

      // Reset form
      setMeetingDate('');
      setMeetingTime('');
      setMeetingType('phone');
      setDuration('30');
      setNotes('');
      setSendInviteToContact(true);
      setAdditionalAttendees('');
      setAddMeetLink(false);
      onClose();
    } catch (error) {
      console.error('Error scheduling meeting:', error);
      toast({
        title: 'Error scheduling meeting',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Schedule Meeting</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" color="gray.600">
              Scheduling meeting with: <strong>{deal?.dealName}</strong>
            </Text>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Date</FormLabel>
              <Input
                type="date"
                value={meetingDate}
                onChange={(e) => setMeetingDate(e.target.value)}
                min={moment().format('YYYY-MM-DD')}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Time</FormLabel>
              <Input
                type="time"
                value={meetingTime}
                onChange={(e) => setMeetingTime(e.target.value)}
              />
            </FormControl>

            <HStack spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">Meeting Type</FormLabel>
                <Select value={meetingType} onChange={(e) => setMeetingType(e.target.value)} size="sm">
                  <option value="phone">Phone Call</option>
                  <option value="video">Video Call</option>
                  <option value="in-person">In-Person</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Duration</FormLabel>
                <Select value={duration} onChange={(e) => setDuration(e.target.value)} size="sm">
                  <option value="15">15 min</option>
                  <option value="30">30 min</option>
                  <option value="45">45 min</option>
                  <option value="60">1 hour</option>
                </Select>
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel fontSize="sm">Notes (Optional)</FormLabel>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Meeting agenda, topics to discuss..."
                size="sm"
                rows={3}
              />
            </FormControl>

            <Divider />

            {/* Calendar Invite Options */}
            <VStack align="stretch" spacing={3}>
              <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                Calendar Invite Options
              </Text>

              {contact?.email && (
                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <FormLabel fontSize="sm" mb={0}>
                    Send invite to contact ({contact.email})
                  </FormLabel>
                  <Switch
                    isChecked={sendInviteToContact}
                    onChange={(e) => setSendInviteToContact(e.target.checked)}
                    colorScheme="blue"
                  />
                </FormControl>
              )}

              <FormControl>
                <FormLabel fontSize="sm">Additional Attendees (Optional)</FormLabel>
                <Input
                  value={additionalAttendees}
                  onChange={(e) => setAdditionalAttendees(e.target.value)}
                  placeholder="email1@example.com, email2@example.com"
                  size="sm"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Separate multiple emails with commas
                </Text>
              </FormControl>

              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel fontSize="sm" mb={0}>
                  Add Google Meet link
                </FormLabel>
                <Switch
                  isChecked={addMeetLink}
                  onChange={(e) => setAddMeetLink(e.target.checked)}
                  colorScheme="green"
                />
              </FormControl>

              {addMeetLink && (
                <Alert status="info" size="sm" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="xs">
                    A Google Meet video link will be generated and included in the calendar invite
                  </Text>
                </Alert>
              )}

              {!contact?.email && !additionalAttendees && (
                <Alert status="warning" size="sm" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="xs">
                    No email addresses provided. Calendar event will be created without invites.
                  </Text>
                </Alert>
              )}
            </VStack>

            {contact?.phone && (
              <Text fontSize="xs" color="gray.600">
                📱 Contact Phone: {contact.phone}
              </Text>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText="Scheduling..."
          >
            Schedule Meeting
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

